import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { BrandSignature } from '@/presentation/components/index.js';
import type { AuthController } from '@/presentation/controllers/auth-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useAuth } from '@/presentation/hooks/use-auth.js';
import { ROUTES } from '@/shared/constants/routes.js';

interface Props {
    controller: AuthController;
}

export function ForgotPasswordPage({ controller }: Props) {
    const { translate } = useApp();
    const { forgotPassword, isLoading } = useAuth(controller);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailError('');
        if (!email.trim()) {
            setEmailError(translate('auth.emailRequired'));
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError(translate('auth.invalidEmail'));
            return;
        }
        const success = await forgotPassword(email.toLowerCase().trim());
        if (success) {
            setSubmitted(true);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                background: 'linear-gradient(135deg, #12122B 0%, #1a1340 50%, #12122B 100%)'
            }}
        >
            <Box sx={{ width: '100%', maxWidth: 400 }}>
                <Box sx={{ mb: 4 }}>
                    <BrandSignature variant="centered" logoHeight={40} />
                </Box>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                    <CardContent sx={{ p: 4 }}>
                        {!submitted ? (
                            <>
                                <Typography variant="h5" fontWeight={700} gutterBottom>
                                    {translate('auth.resetPasswordTitle')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {translate('auth.resetPasswordSubtitle')}
                                </Typography>
                                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        label={translate('auth.email')}
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setEmailError('');
                                        }}
                                        error={!!emailError}
                                        helperText={emailError}
                                        autoComplete="email"
                                        size="small"
                                        fullWidth
                                    />
                                    <Button type="submit" variant="contained" size="large" disabled={isLoading} sx={{ color: 'white' }}>
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : translate('auth.sendResetLink')}
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CheckCircle size={32} color="var(--mui-palette-success-main)" />
                                <Typography variant="h6" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
                                    {translate('auth.checkEmail')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('auth.checkEmailMsg')}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <Link component={RouterLink} to={ROUTES.LOGIN} variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <ArrowLeft size={14} /> {translate('auth.backToLogin')}
                            </Link>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
